export const TUTORIAL_TEMPLATE_ID = 'tutorial';
// @ts-nocheck
/**
 * Generic item / Generic template identity contract
 * ------------------------------------------------
 * DO NOT conflate these four concepts:
 *
 * 1. **Generic item** (canonical persisted identity):
 *    `templateId === "default"` AND `templateElementId === "default"`.
 *    Always created by `createGenericChildObject`. Always renders via
 *    `ItemCanvasElement` — never `TemplateObjectRenderer` / custom layout.
 *    Picker sentinel id `"default"` means this identity from ANY template
 *    context; it must not inherit the surrounding template's id.
 *
 * 2. **Generic template** (shipped seed, formerly display-named "Inventory"):
 *    `templateId === "default"`, display name "Generic". Its only user-facing
 *    item type is the Generic item above — not a separate "Default Item" plus
 *    a synthetic Generic. Seed primary element id is `el-default` (authoring
 *    scaffolding only).
 *
 * 3. **Default / builtin layout** for real template elements:
 *    `renderAsBuiltin: true` → same square surface as Generic items, but still
 *    a themed element (bindings/lifecycle possible). Distinct from (1).
 *
 * 4. **Generic list-root primary** (DEC-050): `metadata.primaryElementId === "default"`
 *    on a themed template. List create persists `{ templateId: theme,
 *    templateElementId: "default" }` so children still inherit the theme, but
 *    the root renders as the generic canvas square with no authored container
 *    element. Distinct from (1): picker Generic children stay `default`/`default`.
 *
 * `DEFAULT_V2_PRIMARY_ELEMENT_ID` (`el-default`) is never a persisted generic
 * identity; `shouldUseGenericCanvasItem` coerces mistaken default/`el-default`
 * pairs to the Generic canvas path and `canonicalGenericElementId` rewrites
 * them to `"default"` for persistence.
 */

export const DEFAULT_TEMPLATE_ID = 'default';
export const DEFAULT_ELEMENT_ID = 'default';
/** V2 template JSON primary element id — not a persisted generic item identity. */
export const DEFAULT_V2_PRIMARY_ELEMENT_ID = 'el-default';

export const isDefaultLikeTemplateId = (templateId?: string | null): boolean => {
  return templateId === DEFAULT_TEMPLATE_ID;
};

export const isDefaultElementId = (elementId?: string | null): boolean => {
  return elementId === DEFAULT_ELEMENT_ID;
};

/** True when `metadata.primaryElementId` is the generic list-root sentinel. */
export const isGenericPrimaryElementId = (elementId?: string | null): boolean => {
  return isDefaultElementId(elementId);
};

/** Map legacy DB value to V2 element id — only for template JSON lookup, not for render routing. */
export function normalizeTemplateElementIdForLookup(elementId?: string | null): string {
  return isDefaultElementId(elementId) ? 'el-default' : (elementId ?? 'el-default');
}

/** Trim and treat null/empty/whitespace as default template id. */
export function normalizeTemplateId(templateId?: string | null): string {
  const trimmed = (templateId ?? '').trim();
  if (!trimmed || isDefaultLikeTemplateId(trimmed)) {
    return DEFAULT_TEMPLATE_ID;
  }
  return trimmed;
}

/** Trim and treat null/empty/whitespace as default element id. */
export function normalizeElementId(elementId?: string | null): string {
  const trimmed = (elementId ?? '').trim();
  if (!trimmed || isDefaultElementId(trimmed)) {
    return DEFAULT_ELEMENT_ID;
  }
  return trimmed;
}

export type TemplateIdentityFields = {
  templateId?: string | null;
  templateElementId?: string | null;
};

/** True when the item should use the generic ItemCanvasElement renderer. */
/* legacy-generic-identity compat (sync-from-containd.sh) */
function __isLegacyGenericIdentity(templateId?: string | null, elementId?: string | null): boolean {
  const t = (templateId ?? '').trim();
  const e = (elementId ?? '').trim();
  return t === TUTORIAL_TEMPLATE_ID || e === DEFAULT_V2_PRIMARY_ELEMENT_ID;
}
export function isGenericItemDefinition(item: TemplateIdentityFields): boolean {
  if (__isLegacyGenericIdentity(item.templateId, item.templateElementId)) return true;
  const templateId = normalizeTemplateId(item.templateId);
  const elementId = normalizeElementId(item.templateElementId);
  return isDefaultLikeTemplateId(templateId) && isDefaultElementId(elementId);
}

/**
 * True when an item should be created or rendered as the generic canvas square
 * (persisted templateElementId "default"), including mistaken "el-default" on
 * the Generic (default) template and themed list-roots whose primary is the
 * `'default'` sentinel. Empty/missing element ids on a themed template are not
 * generic. See file-level contract above.
 */
export function shouldUseGenericCanvasItem(
  templateId?: string | null,
  templateElementId?: string | null,
): boolean {
  if (isGenericItemDefinition({ templateId, templateElementId })) {
    return true;
  }
  const normalizedTemplateId = normalizeTemplateId(templateId);
  const trimmedElementId = (templateElementId ?? '').trim();
  if (trimmedElementId === DEFAULT_ELEMENT_ID) {
    return true;
  }
  const normalizedElementId = normalizeElementId(templateElementId);
  return (
    isDefaultLikeTemplateId(normalizedTemplateId)
    && normalizedElementId === DEFAULT_V2_PRIMARY_ELEMENT_ID
  );
}

/** Canonical persisted element id for generic canvas items. */
export function canonicalGenericElementId(
  templateId?: string | null,
  templateElementId?: string | null,
): string {
  return shouldUseGenericCanvasItem(templateId, templateElementId)
    ? DEFAULT_ELEMENT_ID
    : normalizeElementId(templateElementId);
}
